import { useState, useEffect } from "react";

const STORAGE_KEY = "credit_tracker_v1";

const TAG_STYLES = {
  "soft pull": { bg: "#e6f1fb", color: "#185fa5", label: "soft pull" },
  "hard pull": { bg: "#faeeda", color: "#854f0b", label: "hard pull" },
  caution:     { bg: "#fcebeb", color: "#a32d2d", label: "caution" },
  tip:         { bg: "#eaf3de", color: "#3b6d11", label: "tip" },
  future:      { bg: "#f3f0fb", color: "#6b4db5", label: "pending LLC/EIN" },
};

export default function CreditTracker() {
  const [plan, setPlan]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [activeMonth, setActiveMonth] = useState(null);
  const [checked, setChecked]     = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  useEffect(() => {
    fetch("/plan.json?v=" + Date.now())
      .then((r) => { if (!r.ok) throw new Error("Could not load plan.json"); return r.json(); })
      .then((data) => { setPlan(data); setActiveMonth(data.months[0].id); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(checked)); } catch {}
  }, [checked]);

  const toggle = (key) => setChecked((prev) => ({ ...prev, [key]: !prev[key] }));

  const resetAll = () => {
    if (window.confirm("Reset all checkboxes? This cannot be undone.")) setChecked({});
  };

  if (loading) return (
    <div style={{ fontFamily: "system-ui, sans-serif", padding: 40, color: "#888", textAlign: "center" }}>
      Loading plan...
    </div>
  );

  if (error) return (
    <div style={{ fontFamily: "system-ui, sans-serif", padding: 40, color: "#a32d2d", textAlign: "center" }}>
      <strong>Error:</strong> {error}<br />
      <small>Make sure plan.json is in the public/ folder.</small>
    </div>
  );

  const { metrics, months, tasks } = plan;
  const totalTasks = months.reduce((a, m) => a + (tasks[m.id]?.items.length || 0), 0);
  const doneTasks  = Object.values(checked).filter(Boolean).length;
  const pct        = Math.round((doneTasks / totalTasks) * 100);
  const month      = tasks[activeMonth];

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", maxWidth: 720, margin: "0 auto", padding: "24px 16px", color: "#1a1a1a" }}>

      {/* Header */}
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#888", margin: "0 0 6px" }}>Credit Plan — Phase 2</p>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: "0 0 4px" }}>Mid-Year Execution Tracker</h1>
          <p style={{ fontSize: 14, color: "#666", margin: 0 }}>July – October 2026 · Progress auto-saved</p>
        </div>
        <button onClick={resetAll} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 8, border: "1px solid #ddd", background: "transparent", color: "#888", cursor: "pointer", marginTop: 4 }}>
          Reset all
        </button>
      </div>

      {/* Metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 20 }}>
        {metrics.map((m) => (
          <div key={m.label} style={{ background: "#f5f5f3", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{m.value}</div>
          </div>
        ))}
        <div style={{ background: "#f5f5f3", borderRadius: 10, padding: "12px 14px" }}>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Tasks done</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{doneTasks} / {totalTasks}</div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#888", marginBottom: 6 }}>
          <span>Overall progress</span><span>{pct}%</span>
        </div>
        <div style={{ height: 8, background: "#e8e8e6", borderRadius: 999, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: "#1D9E75", borderRadius: 999, transition: "width 0.3s" }} />
        </div>
      </div>

      {/* Month tabs */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
        {months.map((m) => {
          const monthTasks = tasks[m.id]?.items || [];
          const monthDone  = monthTasks.filter((_, i) => checked[`${m.id}-${i}`]).length;
          const isActive   = activeMonth === m.id;
          return (
            <button key={m.id} onClick={() => setActiveMonth(m.id)} style={{
              padding: "6px 14px", borderRadius: 8,
              border: isActive ? "1.5px solid #1a1a1a" : "1px solid #ddd",
              background: isActive ? "#1a1a1a" : "transparent",
              color: isActive ? "#fff" : "#555",
              fontSize: 13, fontWeight: isActive ? 500 : 400,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            }}>
              {m.short}
              {monthDone > 0 && (
                <span style={{ fontSize: 11, background: isActive ? "rgba(255,255,255,0.2)" : "#e8e8e6", color: isActive ? "#fff" : "#555", borderRadius: 999, padding: "1px 7px" }}>
                  {monthDone}/{monthTasks.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Month panel */}
      {month && (
        <div style={{ background: "#fff", border: "0.5px solid #e0e0de", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: "0.5px solid #e8e8e6" }}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#aaa" }}>
              {months.find((m) => m.id === activeMonth)?.label}
            </p>
            <p style={{ margin: "2px 0 0", fontSize: 15, fontWeight: 500 }}>{month.title}</p>
          </div>

          <div style={{ padding: "4px 0" }}>
            {month.items.map((task, i) => {
              const key   = `${activeMonth}-${i}`;
              const isDone = !!checked[key];
              const tag   = TAG_STYLES[task.tag];
              return (
                <div key={key} onClick={() => toggle(key)} style={{
                  display: "flex", alignItems: "flex-start", gap: 12,
                  padding: "12px 18px",
                  borderBottom: i < month.items.length - 1 ? "0.5px solid #f0f0ee" : "none",
                  cursor: "pointer", background: isDone ? "#fafafa" : "#fff", transition: "background 0.1s",
                }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: 6, flexShrink: 0, marginTop: 1,
                    border: isDone ? "none" : "1.5px solid #ccc",
                    background: isDone ? "#1D9E75" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s",
                  }}>
                    {isDone && <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>✓</span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 14, color: isDone ? "#aaa" : "#1a1a1a", textDecoration: isDone ? "line-through" : "none", lineHeight: 1.5 }}>
                      {task.text}
                    </span>
                    {tag && (
                      <span style={{ display: "inline-block", marginLeft: 8, fontSize: 11, padding: "2px 8px", borderRadius: 999, background: tag.bg, color: tag.color, fontWeight: 500, verticalAlign: "middle" }}>
                        {tag.label}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {month.note && (
            <div style={{ margin: "0 18px 18px", padding: "10px 14px", background: "#f5f5f3", borderLeft: "3px solid #185fa5", borderRadius: "0 8px 8px 0", fontSize: 13, color: "#444", lineHeight: 1.6 }}>
              <strong style={{ color: "#185fa5" }}>Note: </strong>{month.note}
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 16 }}>
        {Object.entries(TAG_STYLES).map(([key, val]) => (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#666" }}>
            <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, background: val.bg, border: `1px solid ${val.color}33` }} />
            {val.label}
          </div>
        ))}
      </div>

      <p style={{ marginTop: 20, fontSize: 11, color: "#bbb", textAlign: "center" }}>
        Edit plan.json to update tasks — no rebuild required
      </p>
    </div>
  );
}
